import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import { supabase } from '../../lib/supabase';
import { Plus, FileText, Percent, Bell, Users } from 'lucide-react';
import { formatCurrencyDecimals } from '../../utils/formatters';
import { EmailTestModeBadge } from '../../components/revenue/EmailTestModeBadge';
import { useRevenueAccess } from '../../hooks/useRevenueAccess';
import {
  isCampaignHotelIncludedInKpis,
  isRevenueHotelSubmissionCompleteStatus,
  revenueCampaignMaterializationPct,
} from '../../utils/revenueCampaignHotelStatus';

/** PostgREST returns at most 1000 rows per request unless paginated. */
const DB_FETCH_BATCH = 1000;

async function fetchAllRows<T extends Record<string, unknown>>(params: {
  table: string;
  columns: string;
  campaignIds: string[];
  orderColumns: string[];
}): Promise<T[]> {
  const list: T[] = [];
  let offset = 0;
  for (;;) {
    let query = supabase
      .from(params.table)
      .select(params.columns)
      .in('campaign_id', params.campaignIds);
    for (const col of params.orderColumns) {
      query = query.order(col, { ascending: true });
    }
    const { data, error } = await query.range(offset, offset + DB_FETCH_BATCH - 1);
    if (error) throw error;
    const rows = (data ?? []) as T[];
    list.push(...rows);
    if (rows.length === 0 || rows.length < DB_FETCH_BATCH) break;
    offset += DB_FETCH_BATCH;
  }
  return list;
}

interface Campaign {
  id: string;
  period_month: number;
  period_year: number;
  name: string | null;
  status: string;
  created_at: string;
  hotels_count?: number;
  total_estimated?: number;
  /**
   * Integer %: sum(realised) / sum(estimated) for form + extra lines of hotels that
   * submitted (same formula as the campaign detail Materialization card).
   */
  materialization_pct?: number | null;
  /** Hotels with a completed submission (`validated` or `invoice_sent`). */
  submissions_validated?: number;
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export default function CampaignDashboard() {
  const navigate = useNavigate();
  const { canMutateRevenue } = useRevenueAccess();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data: campData, error: campErr } = await supabase
        .from('revenue_collection_campaigns')
        .select('id, period_month, period_year, name, status, created_at')
        .order('period_year', { ascending: false })
        .order('period_month', { ascending: false });

      if (campErr) throw campErr;

      const campaignsRaw = campData || [];
      if (campaignsRaw.length === 0) {
        setCampaigns([]);
        return;
      }

      const campaignIds = campaignsRaw.map((c) => c.id);

      type CampaignHotelRow = { campaign_id: string; hotel_id: number; status: string };
      type RevenueRow = {
        campaign_id: string;
        hotel_id: number;
        realised_revenue: number | string | null;
        estimated_revenue: number | string | null;
      };

      const [chRows, responseRows, manualRows] = await Promise.all([
        fetchAllRows<CampaignHotelRow>({
          table: 'revenue_collection_campaign_hotels',
          columns: 'campaign_id, hotel_id, status',
          campaignIds,
          orderColumns: ['campaign_id', 'hotel_id'],
        }),
        fetchAllRows<RevenueRow>({
          table: 'revenue_collection_responses',
          columns: 'campaign_id, hotel_id, realised_revenue, estimated_revenue',
          campaignIds,
          orderColumns: ['campaign_id', 'hotel_id', 'id'],
        }),
        fetchAllRows<RevenueRow>({
          table: 'revenue_collection_manual_lines',
          columns: 'campaign_id, hotel_id, realised_revenue, estimated_revenue',
          campaignIds,
          orderColumns: ['campaign_id', 'hotel_id', 'id'],
        }),
      ]);

      const { data: mvEstimatedRows, error: mvEstErr } = await supabase
        .from('revenue_collection_campaign_mv_estimated')
        .select('campaign_id, total_estimated')
        .in('campaign_id', campaignIds);
      if (mvEstErr) throw mvEstErr;

      const mvEstimatedByCampaignId = new Map<string, number>();
      for (const row of mvEstimatedRows ?? []) {
        const cid = row.campaign_id as string;
        mvEstimatedByCampaignId.set(cid, Number(row.total_estimated) || 0);
      }

      const hotelsByCampaign = new Map<string, CampaignHotelRow[]>();
      for (const row of chRows) {
        const list = hotelsByCampaign.get(row.campaign_id) ?? [];
        list.push(row);
        hotelsByCampaign.set(row.campaign_id, list);
      }

      const validatedHotelsByCampaign = new Map<string, Set<number>>();
      for (const row of chRows) {
        if (!isCampaignHotelIncludedInKpis(row.status)) continue;
        if (!isRevenueHotelSubmissionCompleteStatus(row.status)) continue;
        const cid = row.campaign_id;
        const set = validatedHotelsByCampaign.get(cid) ?? new Set<number>();
        set.add(Number(row.hotel_id));
        validatedHotelsByCampaign.set(cid, set);
      }

      function sumRevenueForSubmitted(
        rows: RevenueRow[],
        campaignId: string,
        validated: Set<number>,
        kind: 'realised' | 'estimated',
      ): number {
        let s = 0;
        for (const row of rows) {
          if (row.campaign_id !== campaignId || !validated.has(Number(row.hotel_id))) continue;
          const raw = kind === 'realised' ? row.realised_revenue : row.estimated_revenue;
          s += Number(raw) || 0;
        }
        return s;
      }

      const list: Campaign[] = campaignsRaw.map((c) => {
        const rows = hotelsByCampaign.get(c.id) ?? [];
        const activeRows = rows.filter((r) => isCampaignHotelIncludedInKpis(r.status as string));
        const submissionsValidated = activeRows.filter((r) =>
          isRevenueHotelSubmissionCompleteStatus(r.status as string),
        ).length;
        const validatedSet = validatedHotelsByCampaign.get(c.id) ?? new Set<number>();

        const totalEstimated = mvEstimatedByCampaignId.get(c.id) ?? 0;

        const realisedSubmitted =
          sumRevenueForSubmitted(responseRows, c.id, validatedSet, 'realised') +
          sumRevenueForSubmitted(manualRows, c.id, validatedSet, 'realised');
        const estimatedSubmitted =
          sumRevenueForSubmitted(responseRows, c.id, validatedSet, 'estimated') +
          sumRevenueForSubmitted(manualRows, c.id, validatedSet, 'estimated');

        const materialization_pct = revenueCampaignMaterializationPct(
          realisedSubmitted,
          estimatedSubmitted,
        );

        return {
          ...c,
          hotels_count: activeRows.length,
          total_estimated: totalEstimated,
          materialization_pct,
          submissions_validated: submissionsValidated,
        };
      });

      setCampaigns(list);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCampaigns();
  }, [loadCampaigns]);

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-amber-100 text-amber-800',
      launched: 'bg-green-100 text-green-800',
      closed: 'bg-slate-100 text-slate-800',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <h1 className="page-title">Revenue Collection</h1>
            <EmailTestModeBadge />
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/revenue-collection/hotel-contacts" className="btn-secondary">
              <Users className="h-4 w-4 shrink-0" aria-hidden /> Hotel contacts
            </Link>
            <Link to="/revenue-collection/commissions" className="btn-secondary">
              <Percent className="h-4 w-4 shrink-0" aria-hidden /> Commissions
            </Link>
            <Link to="/revenue-collection/notifications" className="btn-secondary">
              <Bell className="h-4 w-4 shrink-0" aria-hidden /> Notifications
            </Link>
            <Link to="/revenue-collection/templates" className="btn-secondary">
              <FileText className="h-4 w-4 shrink-0" aria-hidden /> Templates
            </Link>
            {canMutateRevenue ? (
              <Link to="/revenue-collection/campaigns/new" className="btn-primary h-10 py-2">
                <Plus className="w-4 h-4" aria-hidden /> New Campaign
              </Link>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className="text-gray-500">Loading campaigns...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hotels</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Estimated Revenue</th>
                  <th
                    className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase"
                    title="Same as the campaign Materialization card: realised ÷ estimated for hotels that submitted, including extra groups"
                  >
                    Materialization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[10rem]">
                    Submission progress
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaigns.map((c) => {
                  const label = c.name || `${MONTHS[c.period_month - 1]} ${c.period_year}`;
                  const go = () => navigate(`/revenue-collection/campaigns/${c.id}`);
                  return (
                  <tr
                    key={c.id}
                    tabIndex={0}
                    aria-label={`Open campaign: ${label}`}
                    onClick={go}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        go();
                      }
                    }}
                    className="cursor-pointer transition-colors hover:bg-[#148D82]/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-action/35"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {MONTHS[c.period_month - 1]} {c.period_year}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.name || '—'}</td>
                    <td className="px-6 py-4">{statusBadge(c.status)}</td>
                    <td className="px-6 py-4 text-sm text-right">{c.hotels_count ?? 0}</td>
                    <td className="px-6 py-4 text-sm text-right">{formatCurrencyDecimals(c.total_estimated ?? 0)}</td>
                    <td className="px-6 py-4 text-sm text-right tabular-nums">
                      {c.materialization_pct == null ? '—' : `${c.materialization_pct}%`}
                    </td>
                    <td className="px-6 py-4 align-middle">
                      {(() => {
                        const total = c.hotels_count ?? 0;
                        const done = c.submissions_validated ?? 0;
                        const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                        return (
                          <div
                            className="flex w-full min-w-0 items-center justify-between gap-2"
                            title={`${done} of ${total} hotels submitted (validated)`}
                          >
                            <span className="min-w-0 whitespace-nowrap text-xs tabular-nums text-gray-600">
                              <span className="font-medium text-gray-800">{done}</span>
                              <span className="text-gray-400"> / </span>
                              <span>{total}</span>
                              <span className="text-gray-500"> ({total > 0 ? `${pct}%` : '—'})</span>
                            </span>
                            <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200/70">
                              <div
                                className="h-full rounded-full bg-action transition-[width] duration-300 ease-out"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {campaigns.length === 0 && (
              <div className="px-6 py-12 text-center text-gray-500">
                {canMutateRevenue
                  ? 'No campaigns yet. Create one to get started.'
                  : 'No campaigns yet.'}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
